import { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, Clock, Monitor, Users, Video, ChevronLeft, ChevronRight, 
  X, Check, Heart, MessageCircle, RotateCcw, Award, FileText,
  ExternalLink, Copy, CheckCircle, Loader2, AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FooterSection } from "../components/landing/FooterSection";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 이벤트 데이터 생성 (매주 수요일)
const generateEvents = () => {
  const events = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
    const year = currentMonth + monthOffset > 11 ? currentYear + 1 : currentYear;
    const month = (currentMonth + monthOffset) % 12;
    const lastDay = new Date(year, month + 1, 0);
    
    let wednesdayCount = 0;
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      if (date.getDay() === 3) {
        wednesdayCount++;
        if (wednesdayCount <= 3) {
          const ageGroups = ["40대", "50대", "60대"];
          const ageGroup = ageGroups[wednesdayCount - 1];
          const spotsLeft = wednesdayCount === 1 ? 
            (Math.random() < 0.5 ? 0 : Math.floor(Math.random() * 3) + 1) : 
            Math.floor(Math.random() * 6) + 1;
          
          events.push({
            id: `${year}-${month + 1}-${day}`,
            date: new Date(year, month, day),
            day, month, year,
            weekOfMonth: wednesdayCount,
            ageGroup,
            title: `${ageGroup} 줌데이트`,
            time: "저녁 9시",
            location: "Zoom 온라인",
            price: 120,
            description: `${ageGroup} 남녀 각 3명이 참여하는 로테이션 줌 데이트입니다. 30분씩 1:1로 대화하며 서로를 알아갑니다.`,
            includes: ["30분 x 3회 1:1 대화", "전문 MC 진행", "매칭 서비스", "줌 링크 제공"],
            spotsLeft,
          });
        }
      }
    }
  }
  return events;
};

export default function ZoomDatePage() {
  const { user, isAuthenticated, token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  
  const [registering, setRegistering] = useState(false);
  const [myRegistration, setMyRegistration] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [regStep, setRegStep] = useState(1); // 1: 이벤트정보, 2: 결제폼, 3: 서류업로드, 4: 완료
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  
  const isRegularOrAbove = isAuthenticated && user && ['regular', 'premium', 'vip'].includes(user.membership_type);
  const isDocVerified = isAuthenticated && user && user.document_verified;
  const needsDocUpload = !isRegularOrAbove && !isDocVerified;
  
  const isMember = isAuthenticated && user && ['regular', 'premium', 'vip'].includes(user.membership_type);
  const events = useMemo(() => generateEvents(), []);
  
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();
    const days = [];
    
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, isCurrentMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ day, isCurrentMonth: true });
    }
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({ day, isCurrentMonth: false });
    }
    return days;
  }, [currentYear, currentMonth]);
  
  const getEventForDay = (day, month, year) => {
    return events.find(e => e.day === day && e.month === month && e.year === year);
  };
  
  const goToPrevMonth = () => setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  const goToNextMonth = () => setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  
  const monthNames = ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"];
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
  
  const getAgeGroupColor = (ageGroup) => {
    switch (ageGroup) {
      case "40대": return "bg-cyan-500";
      case "50대": return "bg-indigo-500";
      case "60대": return "bg-rose-500";
      default: return "bg-teal-500";
    }
  };

  const fetchEventData = useCallback(async (eventId) => {
    try {
      if (isAuthenticated && token) {
        const mRes = await fetch(`${API_URL}/api/zoom-dates/${eventId}/my-registration`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mRes.ok) {
          const mData = await mRes.json();
          // 결제 완료(paid) 건만 '완료' 화면 표시. 미결제(pending)는 결제 진행 가능하도록 미등록 취급.
          if (mData.registered && mData.registration?.payment_status === "paid") {
            setMyRegistration(mData.registration);
            setRegStep(4);
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [isAuthenticated, token]);

  const handleEventClick = (event) => {
    setMyRegistration(null);
    setRegStep(1);
    setCopiedCode(false);
    setCardNumber('');
    setCardExpiry('');
    setCardCvc('');
    
    if (!isAuthenticated) {
      setSelectedEvent(event);
      setShowMembershipModal(true);
      return;
    }
    setSelectedEvent(event);
    fetchEventData(event.id);
  };

  // Step 1 → Step 2: 결제 폼으로 이동
  const handleGoToPayment = async () => {
    setPaymentProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/zoom-dates/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          event_id: selectedEvent.id,
          price: selectedEvent.price,
          event_title: selectedEvent.title,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "결제 세션을 만들 수 없습니다. 잠시 후 다시 시도해주세요.");
        return;
      }
      if (data.already_registered) {
        setMyRegistration({ zoom_link: data.zoom_link, zoom_code: data.zoom_code, username: user?.username });
        setRegStep(4);
      } else if (data.checkout_url) {
        // PayPal 결제 페이지로 이동
        localStorage.setItem("zoom_reg_id", data.reg_id);
        localStorage.setItem("zoom_event_id", selectedEvent.id);
        window.location.href = data.checkout_url;
      } else {
        alert("결제 페이지를 생성하지 못했습니다. 잠시 후 다시 시도해주세요.");
      }
    } catch (e) {
      alert("결제 처리 중 오류가 발생했습니다.");
    } finally {
      setPaymentProcessing(false);
    }
  };

  // PayPal 결제 승인 후 콜백 처리 (return_url 로 복귀)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");
    const regId = params.get("reg_id") || localStorage.getItem("zoom_reg_id");
    const orderId = params.get("token"); // PayPal 이 붙이는 주문 토큰
    const eventId = localStorage.getItem("zoom_event_id");

    if (paymentStatus === "success" && regId && token) {
      // 복귀 시 이벤트 정보 복원 (완료 모달 표시용)
      if (eventId) {
        const evt = generateEvents().find((e) => e.id === eventId);
        if (evt) setSelectedEvent(evt);
      }
      const confirmPayment = async () => {
        try {
          const res = await fetch(`${API_URL}/api/zoom-dates/confirm-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ reg_id: regId, order_id: orderId }),
          });
          const data = await res.json();
          if (res.ok) {
            setMyRegistration({ zoom_link: data.zoom_link, zoom_code: data.zoom_code, username: user?.username });
            // 비정회원(서류 미인증)은 서류 업로드 단계로, 아니면 완료
            setRegStep(data.needs_doc ? 3 : 4);
          } else {
            alert(data.detail || "결제 확인에 실패했습니다. 다시 시도해주세요.");
          }
          localStorage.removeItem("zoom_reg_id");
          localStorage.removeItem("zoom_event_id");
          window.history.replaceState({}, "", window.location.pathname);
        } catch (e) {
          console.error("Payment confirmation error:", e);
          alert("결제 확인 중 오류가 발생했습니다.");
        }
      };
      confirmPayment();
    } else if (paymentStatus === "cancel") {
      alert("결제가 취소되었습니다.");
      localStorage.removeItem("zoom_reg_id");
      localStorage.removeItem("zoom_event_id");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [token]);

  const formatCardNumber = (value) => {
    const v = value.replace(/\D/g, '').slice(0, 16);
    return v.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\D/g, '').slice(0, 4);
    if (v.length >= 3) return v.slice(0, 2) + '/' + v.slice(2);
    return v;
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_URL}/api/auth/upload-document`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        // 서류 업로드 완료 → 완료 화면 (결제는 이미 완료됨)
        setRegStep(4);
      } else {
        alert("업로드에 실패했습니다. 다시 시도해주세요.");
      }
    } catch {
      alert("업로드 중 오류가 발생했습니다.");
    } finally {
      setUploadingDoc(false);
    }
  };

  const copyZoomCode = () => {
    if (myRegistration?.zoom_code) {
      navigator.clipboard.writeText(myRegistration.zoom_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_6d8c2513-f84b-4244-9ad3-aff494435f58/artifacts/jl8w249x_ChatGPT%20Image%20Mar%2024%2C%202026%2C%2010_51_30%20PM.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 20%',
          }}
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/15" />
        
        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/20 border border-cyan-400/30 rounded-full text-cyan-300 text-sm font-medium mb-6">
              <Video className="w-4 h-4" />
              Online Zoom Dating
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-sans font-[720] text-white mb-6">
              3:3 줌데이트
              <span className="block text-cyan-400 mt-2">로테이션 미팅</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-4">
              싱글 인증 후 누구나 참여 가능!<br />
              같은 연령대 3명의 이성과 1:1로 대화하며 특별한 인연을 찾아보세요
            </p>

            {/* 유료 이벤트 강조 */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500/90 rounded-full text-white font-bold shadow-lg"
              >
                <Award className="w-5 h-5" />
                유료 이벤트 · USD $120
              </motion.div>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500/80 rounded-full text-white font-bold shadow-lg"
              >
                <FileText className="w-5 h-5" />
                혼인관계증명서 제출 필수
              </motion.div>
            </div>

            {/* Info Cards */}
            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              {[
                { icon: Calendar, label: "매주 수요일", value: "저녁 9시 (KST)" },
                { icon: Monitor, label: "장소", value: "Zoom 온라인" },
                { icon: Users, label: "인원", value: "남녀 각 3명" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20"
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-cyan-400" />
                    <div className="text-left">
                      <p className="text-white/60 text-xs">{item.label}</p>
                      <p className="text-white font-semibold">{item.value}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="text-cyan-500 font-medium text-sm tracking-wider uppercase">How It Works</span>
            <h2 className="text-2xl md:text-3xl font-sans font-[720] text-stone-800 mt-2">로테이션 데이팅 방식</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: "1", title: "30분 1:1 대화", desc: "첫 번째 이성과 30분간 대화", icon: MessageCircle },
              { step: "2", title: "로테이션", desc: "다음 이성과 30분간 대화", icon: RotateCcw },
              { step: "3", title: "마지막 대화", desc: "세 번째 이성과 30분간 대화", icon: Heart },
            ].map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-cyan-50">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-xs text-indigo-500 font-bold mb-2">STEP {item.step}</div>
                <h3 className="font-bold text-stone-800 text-lg mb-1">{item.title}</h3>
                <p className="text-stone-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="py-16 bg-stone-100">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <span className="text-cyan-500 font-medium text-sm tracking-wider uppercase">Schedule</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">줌데이트 일정</h2>
          </motion.div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-cyan-600 p-6">
              <div className="flex items-center justify-between">
                <button onClick={goToPrevMonth} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <h3 className="text-2xl font-bold text-white">{currentYear}년 {monthNames[currentMonth]}</h3>
                <button onClick={goToNextMonth} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-200">
              {dayNames.map((day, index) => (
                <div key={day} className={`py-3 text-center text-sm font-semibold ${index === 0 ? "text-red-500" : index === 6 ? "text-blue-500" : "text-stone-600"}`}>
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {calendarDays.map((dayInfo, index) => {
                const event = dayInfo.isCurrentMonth ? getEventForDay(dayInfo.day, currentMonth, currentYear) : null;
                const isToday = dayInfo.isCurrentMonth && dayInfo.day === new Date().getDate() && currentMonth === new Date().getMonth() && currentYear === new Date().getFullYear();
                
                return (
                  <div key={index} className={`min-h-[100px] p-2 border-b border-r border-stone-100 ${!dayInfo.isCurrentMonth ? "bg-stone-50" : "bg-white"} ${event ? "cursor-pointer hover:bg-stone-50 transition-colors" : ""}`}>
                    <div className={`text-sm font-medium mb-1 ${!dayInfo.isCurrentMonth ? "text-stone-300" : index % 7 === 0 ? "text-red-500" : index % 7 === 6 ? "text-blue-500" : "text-stone-700"}`}>
                      <span className={`${isToday ? "bg-indigo-500 text-white w-7 h-7 rounded-full inline-flex items-center justify-center" : ""}`}>
                        {dayInfo.day}
                      </span>
                    </div>
                    
                    {event && (
                      <div className="relative group">
                        <motion.div
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          onClick={() => handleEventClick(event)}
                          className={`${event.spotsLeft > 0 ? getAgeGroupColor(event.ageGroup) : 'bg-stone-400'} rounded-lg p-2 text-white text-xs cursor-pointer hover:opacity-90 transition-opacity`}
                        >
                          <p className="font-bold">{event.ageGroup} 줌</p>
                          <p className="opacity-80">{event.spotsLeft > 0 ? event.time : '마감'}</p>
                        </motion.div>
                        {/* Hover Popup */}
                        <div className="absolute left-full top-0 ml-2 z-50 hidden group-hover:block w-60">
                          <div className="bg-white rounded-xl shadow-xl border border-stone-200 p-4 text-left">
                            <p className="font-bold text-stone-800 text-sm mb-2">{event.title}</p>
                            <div className="space-y-1.5 text-xs text-stone-600">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-stone-400" />
                                <span>{event.year}년 {event.month + 1}월 {event.day}일 (수)</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5 text-stone-400" />
                                <span>{event.time} (KST)</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="w-3.5 h-3.5 text-stone-400" />
                                <span>남녀 각 3명 (잔여 {event.spotsLeft}석)</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Award className="w-3.5 h-3.5 text-amber-500" />
                                <span className="font-semibold text-amber-600">USD ${event.price}</span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                              className="mt-3 w-full py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold rounded-lg transition-colors"
                            >
                              신청하기
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-center gap-6 mt-6">
            {[
              { color: "bg-cyan-500", label: "40대" },
              { color: "bg-indigo-500", label: "50대" },
              { color: "bg-rose-500", label: "60대" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className={`w-4 h-4 ${item.color} rounded`}></div>
                <span className="text-sm text-stone-600">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FooterSection />

      {/* Event Detail + Register Modal */}
      <AnimatePresence>
        {selectedEvent && !showMembershipModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm px-4 py-6 overflow-y-auto"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-lg w-full shadow-2xl my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`${getAgeGroupColor(selectedEvent.ageGroup)} px-5 py-4 text-white relative`}>
                <button onClick={() => setSelectedEvent(null)} className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors" data-testid="close-event-modal">
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Video className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{selectedEvent.title}</h3>
                    <p className="text-white/80 text-sm">{selectedEvent.year}년 {selectedEvent.month + 1}월 {selectedEvent.day}일 (수)</p>
                  </div>
                </div>
              </div>

              <div className="px-5 py-4">
                {/* Event Info - compact row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-600 mb-4">
                  <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-stone-400" />{selectedEvent.time} (한국시간)</span>
                  <span className="flex items-center gap-1.5"><Monitor className="w-4 h-4 text-stone-400" />{selectedEvent.location}</span>
                  <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-stone-400" />{selectedEvent.spotsLeft > 0 ? `남녀 각 3명 (잔여 ${selectedEvent.spotsLeft}석)` : <span className="text-red-500 font-semibold">마감</span>}</span>
                </div>

                {/* 유료 + 혼인관계증명서 - compact */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-center">
                    <p className="text-[10px] text-amber-800 font-bold">유료 이벤트</p>
                    <p className="text-base font-bold text-amber-700">USD ${selectedEvent.price}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-center">
                    <p className="text-[10px] text-red-700 font-bold">필수 서류</p>
                    <p className="text-xs font-bold text-red-600">혼인관계증명서</p>
                  </div>
                </div>

                {/* Step 1: 이벤트 정보 + 결제하기 버튼 */}
                {regStep === 1 && !myRegistration && selectedEvent.spotsLeft > 0 && (
                  <>
                    {/* 단계 표시 */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center gap-1">
                        <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold">1</div>
                        <span className="text-xs font-medium text-indigo-600">결제</span>
                      </div>
                      <div className="flex-1 h-px bg-stone-200" />
                      {needsDocUpload && (
                        <>
                          <div className="flex items-center gap-1">
                            <div className="w-6 h-6 rounded-full bg-stone-200 text-stone-400 flex items-center justify-center text-[10px] font-bold">2</div>
                            <span className="text-xs text-stone-400">서류인증</span>
                          </div>
                          <div className="flex-1 h-px bg-stone-200" />
                        </>
                      )}
                      <div className="flex items-center gap-1">
                        <div className="w-6 h-6 rounded-full bg-stone-200 text-stone-400 flex items-center justify-center text-[10px] font-bold">{needsDocUpload ? "3" : "2"}</div>
                        <span className="text-xs text-stone-400">완료</span>
                      </div>
                    </div>

                    <div className="bg-stone-50 rounded-lg px-3 py-2.5 mb-4">
                      <h4 className="font-semibold text-stone-800 text-sm mb-2">포함 내용</h4>
                      <div className="grid grid-cols-2 gap-1">
                        {selectedEvent.includes.map((item, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-xs text-stone-600">
                            <Check className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={handleGoToPayment}
                      disabled={paymentProcessing}
                      className="w-full py-3 font-bold text-base rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 hover:opacity-90 text-white"
                      data-testid="zoom-date-payment-btn"
                    >
                      {paymentProcessing ? "결제 페이지 이동중..." : `USD $${selectedEvent.price} 결제하기`}
                    </Button>
                  </>
                )}

                {/* Step 3: 혼인관계증명서 업로드 (정회원/서류인증 회원은 스킵) */}
                {regStep === 3 && needsDocUpload && (
                  <>
                    <div className="flex items-center gap-2 mb-5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                          <Check className="w-4 h-4" />
                        </div>
                        <span className="text-sm text-green-600">결제</span>
                      </div>
                      <div className="flex-1 h-px bg-green-300" />
                      <div className="flex items-center gap-1.5">
                        <div className="w-7 h-7 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold">2</div>
                        <span className="text-sm font-medium text-indigo-600">서류인증</span>
                      </div>
                      <div className="flex-1 h-px bg-stone-200" />
                      <div className="flex items-center gap-1.5">
                        <div className="w-7 h-7 rounded-full bg-stone-200 text-stone-400 flex items-center justify-center text-xs font-bold">3</div>
                        <span className="text-sm text-stone-400">완료</span>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-5 text-center">
                      <FileText className="w-10 h-10 text-amber-600 mx-auto mb-3" />
                      <h4 className="font-bold text-stone-800 text-lg mb-2">혼인관계증명서 업로드</h4>
                      <p className="text-sm text-stone-600 mb-1">싱글임을 증명하는 서류를 업로드해주세요.</p>
                      <p className="text-xs text-stone-400">관리자 확인 후 참가가 확정됩니다.</p>
                    </div>

                    <label className="block w-full cursor-pointer mb-4">
                      <div className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors">
                        {uploadingDoc ? (
                          <><Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-2" /><p className="text-sm text-stone-500">업로드 중...</p></>
                        ) : (
                          <>
                            <FileText className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-stone-600">파일 선택하기</p>
                            <p className="text-xs text-stone-400 mt-1">PDF, JPG, PNG (최대 10MB)</p>
                          </>
                        )}
                      </div>
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleDocUpload} disabled={uploadingDoc} data-testid="doc-upload-input" />
                    </label>

                    <button onClick={() => setRegStep(4)} className="w-full text-center text-sm text-stone-400 hover:text-stone-600 transition-colors" data-testid="skip-upload-btn">
                      나중에 제출하기
                    </button>
                  </>
                )}

                {/* 마감 */}
                {regStep === 1 && !myRegistration && selectedEvent.spotsLeft <= 0 && (
                  <div className="bg-stone-100 rounded-xl p-6 text-center mb-5">
                    <AlertTriangle className="w-10 h-10 text-stone-400 mx-auto mb-2" />
                    <p className="font-bold text-stone-600">이번 회차는 마감되었습니다</p>
                    <p className="text-sm text-stone-400 mt-1">다른 주차의 줌데이트를 확인해보세요</p>
                  </div>
                )}

                {/* Step 4: 신청 완료 - 상세 컨펌 */}
                {regStep === 4 && myRegistration && (
                  <div data-testid="zoom-date-code-section">
                    {/* 성공 아이콘 */}
                    <div className="text-center mb-4">
                      <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle className="w-8 h-8 text-emerald-500" />
                      </div>
                      <h3 className="text-lg font-bold text-stone-800">결제가 완료되었습니다!</h3>
                    </div>

                    {/* 컨펌 상세 */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                      <p className="text-sm text-emerald-800 font-medium text-center leading-relaxed">
                        <strong>{selectedEvent.year}년 {selectedEvent.month + 1}월 {selectedEvent.day}일</strong> 3:3 줌데이트<br />
                        참여가 컨펌되었습니다.
                      </p>
                    </div>

                    {/* 안내 사항 */}
                    <div className="bg-stone-50 rounded-xl p-4 mb-4 space-y-2.5">
                      <p className="text-xs text-stone-600 flex items-start gap-2">
                        <Clock className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span>당일 <strong>{selectedEvent.time} (한국시간)</strong>에 아래 링크로 접속해주세요.</span>
                      </p>
                      <p className="text-xs text-stone-600 flex items-start gap-2">
                        <Users className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span>운영진이 이벤트 <strong>24시간 이전</strong>에 개별 연락 드립니다.</span>
                      </p>
                      <p className="text-xs text-stone-600 flex items-start gap-2">
                        <Video className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span>30분 x 3회 1:1 로테이션 대화가 진행됩니다.</span>
                      </p>
                    </div>

                    {/* 줌 코드 & 링크 */}
                    <div className="bg-white border border-stone-200 rounded-xl p-4 mb-4">
                      <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Zoom 미팅 정보</p>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-lg font-bold text-stone-800 font-mono tracking-wider">{myRegistration.zoom_code}</span>
                        <button onClick={copyZoomCode} className="p-2 rounded-lg hover:bg-stone-100 transition-colors" data-testid="copy-zoom-date-code">
                          {copiedCode ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5 text-stone-400" />}
                        </button>
                      </div>
                      <a
                        href={myRegistration.zoom_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm transition-colors"
                        data-testid="zoom-date-join-link"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Zoom 미팅 입장하기
                      </a>
                    </div>

                    {needsDocUpload && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
                        <p className="text-xs text-amber-700">
                          <strong>혼인관계증명서</strong>를 아직 제출하지 않으셨다면 프로필에서 제출해주세요. 미제출 시 참가가 취소될 수 있습니다.
                        </p>
                      </div>
                    )}

                    <Button
                      onClick={() => { setSelectedEvent(null); setRegStep(1); }}
                      className="w-full py-2.5 bg-stone-800 hover:bg-stone-700 text-white rounded-xl text-sm font-medium"
                    >
                      확인
                    </Button>
                  </div>
                )}

              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Membership Required Modal */}
      <AnimatePresence>
        {showMembershipModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => { setShowMembershipModal(false); setSelectedEvent(null); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="w-10 h-10 text-indigo-500" />
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-2">로그인 필요</h3>
                <p className="text-stone-600 mb-6">
                  3:3 줌데이트 참가를 위해 로그인이 필요합니다
                </p>
                <Button
                  onClick={() => { setShowMembershipModal(false); setSelectedEvent(null); }}
                  className="w-full py-4 bg-stone-800 hover:bg-stone-900 text-white rounded-xl"
                >
                  확인
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
